"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Detection,
  detectLanguage,
} from "@/lib/tool-kit/vocabulary/language-detect";

/** English strings. A translated content file overrides these through `ui`. */
export const LANGUAGE_DETECTOR_STRINGS = {
  textLabel: "Text to identify",
  textPlaceholder:
    "Paste a sentence or a paragraph in any of the supported languages.",
  textHint:
    "Detection runs in your browser as you type — nothing is sent anywhere.",
  tryLabel: "Try a sample:",
  sampleLabels: {
    pt: "Portuguese",
    nl: "Dutch",
    uk: "Ukrainian",
    ja: "Japanese",
  } as Record<string, string>,
  samples: {
    pt: "A pesquisa mostra que o sono melhora a memória dos estudantes.",
    nl: "Uit het onderzoek blijkt dat slaap het geheugen van studenten verbetert.",
    uk: "Дослідження показує, що сон покращує пам'ять студентів.",
    ja: "研究によると、睡眠は学生の記憶力を高めます。",
  } as Record<string, string>,
  clear: "Clear",
  emptyPrompt:
    "Type or paste some text to see which language it is most likely in.",
  noLetters:
    "There are no letters to go on — numbers and symbols alone cannot identify a language.",
  heading: "Most likely language",
  statLanguage: "Language",
  statConfidence: "Confidence",
  statScript: "Script",
  byScript: "Certain from the script",
  methodScript:
    "Identified by its script: no other supported language is written in {script}.",
  methodTrigrams:
    "one:Chosen by comparing letter patterns with {count} language written in {script}.|other:Chosen by comparing letter patterns with {count} languages written in {script}.",
  mixedScripts:
    "Only {share} of the letters are in {script}; text in several scripts is labelled by its main one.",
  veryShort:
    "one:Only {count} letter to go on — treat this as a guess. A full sentence gives a far more reliable answer.|other:Only {count} letters to go on — treat this as a guess. A full sentence gives a far more reliable answer.",
  short:
    "Short text: closely related languages are hard to tell apart in a single phrase. Add a sentence or two if the runners-up are close.",
  runnersUp: "Runners-up",
  runnerLabel: "{language}: {probability}",
  noRunnersUp: "Every other language scored below 1%.",
} as const;

type Strings = typeof LANGUAGE_DETECTOR_STRINGS;

const VERY_SHORT = 20;
const SHORT = 60;
const MIXED_BELOW = 0.9;
const RUNNERS_UP = 4;
const VISIBLE_BELOW = 0.01;
const MAX_SHOWN = 0.99;
const SAMPLE_CODES = ["pt", "nl", "uk", "ja"] as const;

/** Language and script names in the page's language, from the browser's own data. */
function useDisplayNames(locale: string) {
  return useMemo(() => {
    const make = (type: "language" | "script") => {
      try {
        const names = new Intl.DisplayNames([locale, "en"], { type });
        return (code: string) => names.of(code) ?? code;
      } catch {
        return (code: string) => code;
      }
    };
    return { language: make("language"), script: make("script") };
  }, [locale]);
}

function Notes({
  detection,
  scriptName,
  strings,
}: {
  detection: Detection;
  scriptName: string;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const notes: string[] = [];
  if (detection.letters < VERY_SHORT) {
    notes.push(
      tPlural(strings.veryShort, locale, detection.letters, {
        count: fmt(detection.letters),
      })
    );
  } else if (detection.letters < SHORT && detection.method === "trigrams") {
    notes.push(strings.short);
  }
  if (detection.scriptShare < MIXED_BELOW) {
    notes.push(
      t(strings.mixedScripts, {
        share: fmt(detection.scriptShare, {
          style: "percent",
          maximumFractionDigits: 0,
        }),
        script: scriptName,
      })
    );
  }
  return (
    <>
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
    </>
  );
}

function Results({
  detection,
  strings,
}: {
  detection: Detection;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const names = useDisplayNames(locale);
  const [best, ...rest] = detection.candidates;
  const scriptName = names.script(detection.script);
  const percent = (probability: number) =>
    fmt(Math.min(probability, MAX_SHOWN), {
      style: "percent",
      maximumFractionDigits: 0,
    });
  const runners = rest
    .slice(0, RUNNERS_UP)
    .filter((candidate) => candidate.probability >= VISIBLE_BELOW);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.heading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statLanguage}
          value={names.language(best.code)}
        />
        <StatCard
          label={strings.statConfidence}
          value={
            detection.method === "script"
              ? strings.byScript
              : percent(best.probability)
          }
        />
        <StatCard label={strings.statScript} value={scriptName} />
      </StatGrid>
      <p className={HINT}>
        {detection.method === "script"
          ? t(strings.methodScript, { script: scriptName })
          : tPlural(strings.methodTrigrams, locale, detection.compared, {
              count: fmt(detection.compared),
              script: scriptName,
            })}
      </p>
      <Notes detection={detection} scriptName={scriptName} strings={strings} />
      {detection.method === "trigrams" ? (
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.runnersUp}</h3>
          {runners.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {strings.noRunnersUp}
            </p>
          ) : (
            <ul className="space-y-2">
              {runners.map((candidate) => (
                <li className="space-y-1" key={candidate.code}>
                  <p className="text-foreground text-sm">
                    {t(strings.runnerLabel, {
                      language: names.language(candidate.code),
                      probability: percent(candidate.probability),
                    })}
                  </p>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary/60"
                      style={{
                        width: `${Math.round(candidate.probability * 100)}%`,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

export default function LanguageDetectorWidget() {
  const strings = useToolUi(LANGUAGE_DETECTOR_STRINGS);
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const detection = useMemo(() => detectLanguage(deferred), [deferred]);

  function body() {
    if (!deferred.trim()) {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (!detection) {
      return <Notice>{strings.noLetters}</Notice>;
    }
    return <Results detection={detection} strings={strings} />;
  }

  return (
    <>
      <TextAreaField
        hint={strings.textHint}
        id="langdetect-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={6}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <span className={LABEL}>{strings.tryLabel}</span>
        {SAMPLE_CODES.map((code) => (
          <button
            className={BUTTON_SECONDARY}
            key={code}
            onClick={() => setText(strings.samples[code])}
            type="button"
          >
            {strings.sampleLabels[code]}
          </button>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {body()}
    </>
  );
}
